const DEFAULT_THRESHOLD = 1;
const TRAILING_COUNT = 3;

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

const serviceMonthCode = (value) => {
  const raw = String(value || '').toUpperCase();
  const iso = raw.match(/^(\d{4})-(\d{2})-\d{2}$/);
  if (iso) return MONTHS[Number(iso[2]) - 1] || raw;
  return raw;
};

/**
 * Returns the list of month codes in the trailing window ending at `current`,
 * newest first. e.g. trailingWindow('APR', 3) === ['APR', 'MAR', 'FEB']
 */
export const trailingWindow = (current, count = TRAILING_COUNT) => {
  const normalized = serviceMonthCode(current);
  const idx = MONTHS.indexOf(normalized);
  if (idx < 0) return [normalized];
  const start = Math.max(0, idx - (count - 1));
  return MONTHS.slice(start, idx + 1).reverse();
};

const sumBy = (rows, pick) => rows.reduce((acc, r) => acc + (pick(r) || 0), 0);
const roundCents = (n) => Math.round(n * 100) / 100;

/**
 * Derive a short, UI-displayable "variance driver" diagnostic from a row's
 * shape — the likely reason this BAC is in variance. Returns an object with
 * a label, optional detail, and a color hint so the UI can color-code it.
 *
 * Heuristics (in priority order):
 *   1. Missing sources win — the biggest actionable signal.
 *   2. If all three sources present, compare pairwise deltas to spot which
 *      source is the "odd one out" (e.g. DPE drift vs CPQ stale).
 *   3. Layer on pending-status flags (PENDING LIVE / PENDING CANCEL in CPQ).
 *   4. Flag GM credit rows as "Credit applied this cycle".
 */
export const deriveVarianceDriver = ({
  gmBillable,
  cpqBillable,
  dpeBillable,
  cpqLineItems,
  gmLineItems,
  deltas,
  threshold,
}) => {
  const missingGm = gmBillable.length === 0;
  const missingCpq = cpqBillable.length === 0;
  const missingDpe = dpeBillable.length === 0;
  const missingCount = [missingGm, missingCpq, missingDpe].filter(Boolean).length;

  const pendingLiveInCpq = cpqLineItems.some((r) => r.productStatus === 'PENDING LIVE');
  const pendingCancelInCpq = cpqLineItems.some((r) => r.productStatus === 'PENDING CANCEL');
  const gmHasCredits = gmLineItems.some(
    (r) => r.isCurrentMonth && Number(r.budget) < 0,
  );

  // Missing patterns
  if (missingCount === 3) return { label: 'No data anywhere', color: 'error' };
  if (missingCount === 2) {
    const present = !missingGm ? 'GM' : !missingCpq ? 'CPQ' : 'DPE';
    return { label: `Only in ${present}`, color: 'error' };
  }
  if (missingGm) return { label: 'Missing in GM', color: 'warning' };
  if (missingCpq) return { label: 'Missing in CPQ', color: 'warning' };
  if (missingDpe) return { label: 'Missing in DPE', color: 'info' };

  // Everything present — pairwise analysis
  const gmVsCpqAbs = Math.abs(deltas.gmVsCpq);
  const gmVsDpeAbs = Math.abs(deltas.gmVsDpe);
  const cpqVsDpeAbs = Math.abs(deltas.cpqVsDpe);

  const cpqMatchesDpe = cpqVsDpeAbs <= threshold;
  const gmMatchesDpe = gmVsDpeAbs <= threshold;
  const gmMatchesCpq = gmVsCpqAbs <= threshold;

  // GM ↔ CPQ agree: variance is DPE-only (informational)
  if (gmMatchesCpq) {
    if (!gmMatchesDpe) return { label: 'DPE drift', color: 'info' };
    // Everything matches
    if (pendingLiveInCpq) return { label: 'Pending activation', color: 'info' };
    if (pendingCancelInCpq) return { label: 'Wind-down in progress', color: 'warning' };
    return null;
  }

  // GM ↔ CPQ disagree — classify by which source agrees with DPE
  let base;
  if (cpqMatchesDpe) {
    base = { label: 'CPQ stale vs GM', color: 'warning' };
  } else if (gmMatchesDpe) {
    base = { label: 'GM billing lag', color: 'warning' };
  } else {
    base = { label: 'Three-way drift', color: 'error' };
  }

  // Overlay contextual flags
  const suffix = [];
  if (pendingLiveInCpq) suffix.push('pending activation');
  if (pendingCancelInCpq) suffix.push('wind-down');
  if (gmHasCredits) suffix.push('credit applied');
  if (suffix.length) base = { ...base, detail: suffix.join(' · ') };
  return base;
};

/**
 * Reconcile the three sources into one variance row per BAC.
 *
 * Totals are computed from filtered subsets:
 *   - GM:  rows where serviceMonth === the selected month
 *   - CPQ: rows where isBilling === true
 *   - DPE: products where isBilling === true
 *
 * However, the returned line-item arrays retain *all* rows so the UI can
 * group them (billing vs not-billing) and show trailing-month context.
 *
 * @param {object} args
 * @param {object[]} args.gmRows      - normalized GM rows
 * @param {object[]} args.cpqRows     - normalized CPQ rows
 * @param {object[]} args.dpeDealers  - seed DPE dealers [{ bac, dealerName, brand, products[] }]
 * @param {string}   args.serviceMonth- e.g. 'APR'
 * @param {number}   [args.threshold] - dollars, default 1
 * @param {number}   [args.trailing]  - months in trailing window, default 3
 * @returns {object[]} variance rows
 */
export const reconcile = ({
  gmRows,
  cpqRows,
  dpeDealers,
  serviceMonth,
  threshold = DEFAULT_THRESHOLD,
  trailing = TRAILING_COUNT,
}) => {
  const byBac = new Map();
  const currentServiceMonth = serviceMonthCode(serviceMonth);
  const window = trailingWindow(currentServiceMonth, trailing);

  const ensure = (bac, dealerName, brand) => {
    if (!byBac.has(bac)) {
      byBac.set(bac, {
        bac,
        dealerName: dealerName || '',
        brand: brand || '',
        gmLineItems: [],
        cpqLineItems: [],
        dpeLineItems: [],
      });
    }
    const rec = byBac.get(bac);
    if (!rec.dealerName && dealerName) rec.dealerName = dealerName;
    if (!rec.brand && brand) rec.brand = brand;
    return rec;
  };

  // GM: keep rows within the trailing window, tagged with isCurrentMonth
  for (const r of gmRows) {
    if (!window.includes(r.serviceMonth)) continue;
    ensure(r.dealerCode, r.dealerName).gmLineItems.push({
      ...r,
      isCurrentMonth: r.serviceMonth === currentServiceMonth,
    });
  }

  // CPQ: keep all rows for the BAC
  for (const r of cpqRows) {
    ensure(r.bac, r.dealerName).cpqLineItems.push(r);
  }

  // DPE: keep all products for the dealer
  for (const d of dpeDealers) {
    const rec = ensure(d.bac, d.dealerName, d.brand);
    rec.dpeLineItems.push(...d.products);
  }

  const rows = [];
  for (const rec of byBac.values()) {
    const gmBillable = rec.gmLineItems.filter((r) => r.isCurrentMonth);
    const cpqBillable = rec.cpqLineItems.filter((r) => r.isBilling);
    const dpeBillable = rec.dpeLineItems.filter((p) => p.isBilling);

    const gmTotal = roundCents(sumBy(gmBillable, (r) => r.budget));
    const cpqTotal = roundCents(sumBy(cpqBillable, (r) => r.dealerCost));
    const dpeTotal = roundCents(sumBy(dpeBillable, (p) => p.dealerPrice));

    const missingGm = gmBillable.length === 0;
    const missingCpq = cpqBillable.length === 0;
    const missingDpe = dpeBillable.length === 0;

    const deltas = {
      gmVsCpq: roundCents(gmTotal - cpqTotal),
      gmVsDpe: roundCents(gmTotal - dpeTotal),
      cpqVsDpe: roundCents(cpqTotal - dpeTotal),
    };
    const maxAbs = Math.max(
      Math.abs(deltas.gmVsCpq),
      Math.abs(deltas.gmVsDpe),
      Math.abs(deltas.cpqVsDpe),
    );

    let status = 'match';
    if (missingGm && !missingCpq && !missingDpe) status = 'missing-gm';
    else if (missingCpq && !missingGm && !missingDpe) status = 'missing-cpq';
    else if (missingDpe && !missingGm && !missingCpq) status = 'missing-dpe';
    else if (missingGm || missingCpq || missingDpe) status = 'missing-multiple';
    else if (maxAbs > threshold) status = 'variance';

    const varianceDriver = deriveVarianceDriver({
      gmBillable,
      cpqBillable,
      dpeBillable,
      cpqLineItems: rec.cpqLineItems,
      gmLineItems: rec.gmLineItems,
      deltas,
      threshold,
    });

    rows.push({
      ...rec,
      gmTotal,
      cpqTotal,
      dpeTotal,
      deltas,
      maxAbsDelta: roundCents(maxAbs),
      status,
      varianceDriver,
      trailingWindow: window,
      serviceMonth: currentServiceMonth,
    });
  }

  const statusRank = {
    'missing-multiple': 0,
    variance: 1,
    'missing-gm': 2,
    'missing-cpq': 2,
    'missing-dpe': 2,
    match: 5,
  };
  rows.sort((a, b) => {
    if (statusRank[a.status] !== statusRank[b.status]) return statusRank[a.status] - statusRank[b.status];
    if (a.maxAbsDelta !== b.maxAbsDelta) return b.maxAbsDelta - a.maxAbsDelta;
    return String(a.bac).localeCompare(String(b.bac));
  });

  return rows;
};

/**
 * Summarize the reconciliation for the top-of-page metrics.
 *
 * The cycle goal is ZERO GM↔CPQ variance. DPE variance is informational and
 * does not gate the cycle. So the money totals are computed against |GM - CPQ|
 * only. Reviewed BACs shift their delta from the "remaining" bucket to the
 * "cleared" bucket — when remainingGmCpqAbs hits $0 the cycle is done.
 *
 * @param {object[]} rows     - output of reconcile(), each row optionally
 *                              carrying a `review: { reviewed: bool }` field
 * @param {number}   [threshold] - dollar threshold for "matching"
 */
export const summarize = (rows, threshold = DEFAULT_THRESHOLD) => {
  const summary = {
    totalBacs: rows.length,
    matching: 0,      // |GM - CPQ| <= threshold
    toReview: 0,      // |GM - CPQ| > threshold AND not reviewed
    reviewed: 0,      // |GM - CPQ| > threshold AND reviewed
    inheritedResolved: 0,
    missingCount: 0,  // any source missing (informational)
    remainingGmCpqAbs: 0,
    clearedGmCpqAbs: 0,
    totalGmCpqAbs: 0,
  };
  for (const r of rows) {
    const gmCpqAbs = Math.abs(r.deltas?.gmVsCpq || 0);
    const isReviewed = Boolean(r.review?.reviewed);
    if (r.review?.inheritedResolved) summary.inheritedResolved += 1;
    summary.totalGmCpqAbs += gmCpqAbs;
    if (r.status && String(r.status).startsWith('missing')) summary.missingCount += 1;

    if (gmCpqAbs <= threshold) {
      summary.matching += 1;
    } else if (isReviewed) {
      summary.reviewed += 1;
      summary.clearedGmCpqAbs += gmCpqAbs;
    } else {
      summary.toReview += 1;
      summary.remainingGmCpqAbs += gmCpqAbs;
    }
  }
  summary.remainingGmCpqAbs = roundCents(summary.remainingGmCpqAbs);
  summary.clearedGmCpqAbs = roundCents(summary.clearedGmCpqAbs);
  summary.totalGmCpqAbs = roundCents(summary.totalGmCpqAbs);
  return summary;
};

export const STATUS_META = {
  match: { label: 'Match', color: 'success' },
  variance: { label: 'Variance', color: 'error' },
  'missing-gm': { label: 'Missing in GM', color: 'warning' },
  'missing-cpq': { label: 'Missing in CPQ', color: 'warning' },
  'missing-dpe': { label: 'Missing in DPE', color: 'warning' },
  'missing-multiple': { label: 'Missing in multiple', color: 'error' },
};
